import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, CheckCircleIcon, XMarkIcon, ClipboardIcon, CheckIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../utils/api';
import { PaymentRequest } from '../types/api';
import MarkPaidModal from '../components/PaymentRequest/MarkPaidModal';

const PaymentRequestsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid'>('all');
  const [businessTypeFilter, setBusinessTypeFilter] = useState<'all' | 'fish' | 'goat'>('all');
  const [selectedRequest, setSelectedRequest] = useState<PaymentRequest | null>(null);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedQRImage, setSelectedQRImage] = useState<string | null>(null);
  const [copiedUPIId, setCopiedUPIId] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  useEffect(() => {
    if (selectedLocation?.id) {
      loadRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, statusFilter, businessTypeFilter]);

  // Handle ESC key to close QR modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedQRImage) {
        setSelectedQRImage(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedQRImage]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      if (selectedLocation?.id) {
        params.append('location_id', selectedLocation.id);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (businessTypeFilter !== 'all') {
        params.append('business_type', businessTypeFilter);
      }
      // Include QR images for display
      params.append('include_qr_images', 'true');
      
      const response = await apiClient.get(`/payment-requests?${params.toString()}`);
      setRequests(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load payment requests'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUPI = async (upiId: string) => {
    try {
      await navigator.clipboard.writeText(upiId);
      setCopiedUPIId(upiId);
      setTimeout(() => setCopiedUPIId(null), 2000); // Reset after 2 seconds
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = upiId;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedUPIId(upiId);
        setTimeout(() => setCopiedUPIId(null), 2000);
      } catch (fallbackErr) {
        setError('Failed to copy UPI ID');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleMarkPaid = (request: PaymentRequest) => {
    setSelectedRequest(request);
    setShowMarkPaidModal(true);
  };

  const handleMarkPaidSuccess = () => {
    setShowMarkPaidModal(false);
    setSelectedRequest(null);
    loadRequests();
  };

  const handleDelete = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to delete this payment request?')) {
      return;
    }

    try {
      setDeletingId(requestId);
      await apiClient.delete(`/payment-requests/${requestId}`);
      await loadRequests();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete payment request'));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExpenseTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'salary': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'diesel': return 'bg-gray-100 text-gray-800';
      case 'construction': return 'bg-orange-100 text-orange-800';
      case 'goat_purchase': return 'bg-indigo-100 text-indigo-800';
      case 'misc': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getExpenseTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      salary: 'Salary',
      maintenance: 'Maintenance',
      diesel: 'Diesel/Fuel',
      construction: 'Construction',
      goat_purchase: 'Goat Purchase',
      misc: 'Miscellaneous',
    };
    return labels[type.toLowerCase()] || type;
  };

  const filteredRequests = requests.filter(req => {
    if (statusFilter !== 'all' && req.payment_status !== statusFilter) return false;
    if (businessTypeFilter !== 'all' && req.business_type !== businessTypeFilter) return false;
    return true;
  });

  const pendingRequests = requests.filter(r => r.payment_status === 'pending');
  const totalPendingAmount = pendingRequests.reduce((sum, r) => sum + r.amount, 0);

  // Calculate monthly paid totals
  const paidRequests = requests.filter(r => r.payment_status === 'paid' && r.payment_date);
  const monthlyPaidTotals = paidRequests.reduce((acc, r) => {
    if (!r.payment_date) return acc;
    const paymentDate = new Date(r.payment_date);
    const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = paymentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { label: monthLabel, total: 0 };
    }
    acc[monthKey].total += r.amount;
    return acc;
  }, {} as Record<string, { label: string; total: number }>);

  // Sort months by date (most recent first)
  const sortedMonthlyTotals = Object.entries(monthlyPaidTotals)
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, 6); // Show last 6 months

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💰 Payment Requests</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
        {(isManager || isAdmin) && (
          <Link
            to="/payment-requests/new"
            className="btn-touch bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>New Request</span>
          </Link>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Card */}
      {pendingRequests.length > 0 && (
        <div className="card p-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Pending Requests</span>
            <span className="text-lg font-bold text-yellow-600">
              {pendingRequests.length} request{pendingRequests.length !== 1 ? 's' : ''} - ₹{totalPendingAmount.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="form-input"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div>
            <label className="form-label">Business Type</label>
            <select
              value={businessTypeFilter}
              onChange={(e) => setBusinessTypeFilter(e.target.value as any)}
              className="form-input"
            >
              <option value="all">All</option>
              <option value="fish">Fish</option>
              <option value="goat">Goat</option>
            </select>
          </div>
        </div>
      </div>

      {/* Monthly Paid Totals */}
      {sortedMonthlyTotals.length > 0 && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Money Paid Per Month</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {sortedMonthlyTotals.map(([key, { label, total }]) => (
              <div key={key} className="text-center">
                <div className="text-xs text-gray-500 mb-1">{label}</div>
                <div className="text-sm font-bold text-green-600">₹{total.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Requests List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Requests ({filteredRequests.length})
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : filteredRequests.length > 0 ? (
          <div className="space-y-3">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className={`border rounded-lg p-4 ${
                  request.payment_status === 'pending'
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-green-200 bg-green-50'
                }`}
              >
                <div className="flex flex-col">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-bold text-lg">
                        ₹{request.amount.toLocaleString()}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${getExpenseTypeColor(request.expense_type)}`}>
                        {getExpenseTypeLabel(request.expense_type)}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                        request.payment_status === 'pending'
                          ? 'bg-yellow-200 text-yellow-800'
                          : 'bg-green-200 text-green-800'
                      }`}>
                        {request.payment_status === 'pending' ? 'Pending' : 'Paid'}
                      </span>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">
                        {request.business_type === 'fish' ? 'Fish' : 'Goat'}
                      </span>
                      {request.payment_status === 'pending' && isAdmin && (
                        <button
                          onClick={() => handleMarkPaid(request)}
                          className="btn-touch bg-blue-600 text-white px-2 py-0.5 rounded text-xs hover:bg-blue-700 flex items-center space-x-1 whitespace-nowrap ml-auto"
                        >
                          <CheckCircleIcon className="h-3 w-3" />
                          <span>Mark Paid</span>
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="font-medium break-words">{request.description}</div>
                      {request.upi_id && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="whitespace-nowrap">UPI ID:</span>
                          <span className="font-mono break-all">{request.upi_id}</span>
                          <button
                            onClick={() => handleCopyUPI(request.upi_id!)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                            title="Copy UPI ID"
                          >
                            {copiedUPIId === request.upi_id ? (
                              <CheckIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <ClipboardIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                      {request.name_on_upi && (
                        <div>Name on UPI: <span className="font-medium">{request.name_on_upi}</span></div>
                      )}
                      {request.qr_image_base64 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">QR Code:</div>
                          <img 
                            src={request.qr_image_base64} 
                            alt="QR Code" 
                            className="w-32 h-32 border border-gray-300 rounded-lg object-contain bg-white cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedQRImage(request.qr_image_base64 || null)}
                            title="Click to view larger"
                          />
                        </div>
                      )}
                      {request.requested_by_name && (
                        <div>Requested by: {request.requested_by_name}</div>
                      )}
                      {request.pond_name && (
                        <div>Pond: {request.pond_name}</div>
                      )}
                      {request.shed_name && (
                        <div>Shed: {request.shed_name}</div>
                      )}
                      {request.payment_status === 'paid' && request.paid_by_name && (
                        <div className="text-green-700">Paid by: {request.paid_by_name}</div>
                      )}
                      {request.payment_reference && (
                        <div className="text-xs">Transaction ID: {request.payment_reference}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      Request Date: {formatDate(request.request_date)}
                      {request.payment_date && ` • Paid: ${formatDate(request.payment_date)}`}
                    </div>
                  </div>
                  {/* Action buttons - only for pending requests */}
                  {request.payment_status === 'pending' && (
                    <div className="flex justify-end gap-2 mt-2">
                      {/* Edit button - Managers can edit own, Admins can edit any */}
                      {(isAdmin || (isManager && request.requested_by === user?.id)) && (
                        <button
                          onClick={() => navigate(`/payment-requests/${request.id}/edit`)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit request"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {/* Delete button - Managers can delete own, Admins can delete any */}
                      {(isAdmin || (isManager && request.requested_by === user?.id)) && (
                        <button
                          onClick={() => handleDelete(request.id)}
                          disabled={deletingId === request.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete request"
                        >
                          {deletingId === request.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  {/* No delete button for paid requests - Managers cannot delete paid requests */}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">💰</div>
            <p className="text-gray-500">No payment requests found</p>
            {(isManager || isAdmin) && (
              <Link
                to="/payment-requests/new"
                className="mt-4 inline-block text-blue-600 hover:text-blue-800"
              >
                Create your first payment request
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Mark Paid Modal */}
      {showMarkPaidModal && selectedRequest && (
        <MarkPaidModal
          request={selectedRequest}
          onClose={() => {
            setShowMarkPaidModal(false);
            setSelectedRequest(null);
          }}
          onSuccess={handleMarkPaidSuccess}
        />
      )}

      {/* QR Code Full Screen Modal */}
      {selectedQRImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedQRImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedQRImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2"
              aria-label="Close"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            <img 
              src={selectedQRImage} 
              alt="QR Code Full Screen" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg bg-white p-4"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-white text-sm">
              Click outside or close button to exit
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRequestsPage;

